export type LevelId =
  "sp1" | "sp2" | "sp3" | "sp4" | "sp5" | "sp6" | "sp7" | "sp8" | "lo1" | "lo2" | "lo3" | "lo4";

/** Levels offered in the game (klasa 1–3 exist in the type system but are not offered: too young). */
export const LEVEL_IDS: readonly LevelId[] = ["sp4", "sp5", "sp6", "sp7", "sp8", "lo1", "lo2", "lo3", "lo4"];

export type Tier = 1 | 2 | 3;
export type Lang = "pl" | "en";

export interface LocalizedText {
  pl: string;
  en: string;
}

export type AnswerMode = "choice4" | "numeric" | "fraction" | "numericSet" | "truefalse" | "text";

export type Answer =
  | { mode: "choice4"; choices: LocalizedText[]; correctIndex: number }
  | { mode: "numeric"; value: number; tolerance?: number; unit?: LocalizedText }
  | { mode: "fraction"; num: number; den: number; acceptEquivalent: boolean }
  | { mode: "numericSet"; values: number[] }
  | { mode: "truefalse"; value: boolean }
  /** Short typed text such as "XIV" or "5x+3"; compared after normalisation, `accept` lists alternatives. */
  | { mode: "text"; value: string; accept?: string[] };

export interface Visual {
  kind: "objects" | "clock" | "shape" | "numberline" | "grid";
  data: Record<string, unknown>;
}

/** What a generator returns; the registry adds id/topic/tier/seed. */
export interface QuestionDraft {
  prompt: LocalizedText;
  /** Canonical answer (typed mode). */
  answer: Answer;
  /** Optional multiple-choice version of the same answer, used when the player prefers buttons. */
  choice4?: Extract<Answer, { mode: "choice4" }>;
  explanation: { pl: string[]; en: string[] };
  visual?: Visual;
  lessonId?: string;
}

export interface Question extends QuestionDraft {
  id: string;
  topicId: string;
  tier: Tier;
  seed: number;
}

export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform integer in [min, max] (inclusive). */
  int(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  shuffle<T>(items: readonly T[]): T[];
  chance(probability: number): boolean;
}

export type Generator = (rng: Rng, tier: Tier) => QuestionDraft;

export interface TopicDef {
  id: string;
  level: LevelId;
  name: LocalizedText;
  modes: AnswerMode[];
  generate: Generator;
}

export interface TopicStat {
  attempts: number;
  correct: number;
  avgTimeMs: number;
  lastSeen?: string;
}

export type MonsterRank = "common" | "elite" | "boss";

/** How the player wants to answer: buttons, typing, or the level default. */
export type AnswerPreference = "auto" | "choice" | "typed";
